import { action, computed, makeObservable, override } from 'mobx';
import { useContext, createContext } from 'react';
import { redirectToRoute } from 'routes/redirectToRoute';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import QuoteFlowDelegate from 'store/flows/quote/quoteFlowDelegate';
import IHealthQuestionnaireEntryPage from 'store/modules/subscriber/health-questionnaire/iHealthQuestionnaireEntryPage';
import { HealthQuestionnaireDomainParams } from 'store/modules/subscriber/health-questionnaire/types';

export interface ImqBrokerHealthQuestionnaireDomainParams extends HealthQuestionnaireDomainParams {
  periodId: string;
  cartId: string;
}

export default class ImqBrokerHealthQuestionnaireEntryPageStore extends IHealthQuestionnaireEntryPage {
  protected readonly _domainParams: ImqBrokerHealthQuestionnaireDomainParams;
  public readonly flowDelegate: QuoteFlowDelegate;

  public override get domainParams(): ImqBrokerHealthQuestionnaireDomainParams {
    return this._domainParams;
  }

  public get isInitialLoading(): boolean {
    return this.flowDelegate.isFlowDataLoading || this.isPageContentLoading;
  }

  protected redirectWhenQuestionnaireAlreadyStarted(): void {
    const { groupId, periodId, cartId, subscriberId, entityId } = this.domainParams;
    redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE, {
      groupId,
      periodId,
      cartId,
      subscriberId,
      entityId,
    });
  }

  protected redirectAfterAnswer(hasConditions: boolean): void {
    const { groupId, periodId, cartId, subscriberId, entityId } = this.domainParams;

    if (hasConditions) {
      redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE, {
        groupId,
        periodId,
        cartId,
        subscriberId,
        entityId,
      });
      return;
    }

    redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES, { groupId, periodId, cartId });
  }

  public async getFlowAndPageInitialData(): Promise<void> {
    this.flowDelegate.setIsInitialDataLoading(true);
    await this.flowDelegate.getFlowInitialData();
    await this.getInitialData();
    this.flowDelegate.setIsInitialDataLoading(false);
  }

  constructor(domainParams: ImqBrokerHealthQuestionnaireDomainParams) {
    super(domainParams);
    this._domainParams = domainParams;
    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: {
        groupId: domainParams.groupId,
        periodId: domainParams.periodId,
        cartId: domainParams.cartId,
      },
      nextPageId: QuoteRouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE,
    });

    makeObservable<
      ImqBrokerHealthQuestionnaireEntryPageStore,
      'redirectWhenQuestionnaireAlreadyStarted' | 'redirectAfterAnswer'
    >(this, {
      isInitialLoading: computed,
      domainParams: override,
      redirectWhenQuestionnaireAlreadyStarted: action.bound,
      redirectAfterAnswer: action.bound,
      getFlowAndPageInitialData: action.bound,
    });
  }
}

const ImqBrokerHealthQuestionnaireEntryPageStoreContext = createContext(
  new ImqBrokerHealthQuestionnaireEntryPageStore({} as ImqBrokerHealthQuestionnaireDomainParams),
);

export const useImqBrokerHealthQuestionnaireEntryPageStore = (): ImqBrokerHealthQuestionnaireEntryPageStore => {
  return useContext(ImqBrokerHealthQuestionnaireEntryPageStoreContext);
};

export { ImqBrokerHealthQuestionnaireEntryPageStoreContext };
